import { ComboboxProvider, SelectProvider } from '@ariakit/react'
import type { FC } from 'react'
import { useState } from 'react'
import { InputHelpText } from '@/components/ui/input-help-text'
import {
	SelectComboboxInput,
	SelectComboboxList,
	SelectComboboxPopover,
	SelectItem,
	SelectTrigger,
	SelectVirtualList
} from '@/components/ui/select-atoms-legacy'
import { Skeleton } from '@/components/ui/skeleton'
import { useGetGeos } from '@/features/subgeo/queries/use-get-geos'
import { debounce } from '@/helpers/utils'
import { useTranslation } from '@/translations/i18n'

interface GeoSelectProps {
	errorMessage?: string
	onChange: (value: string) => void
	value: string
	viewOnly?: boolean
}

export const GeoSelect: FC<GeoSelectProps> = ({
	errorMessage,
	onChange,
	value,
	viewOnly
}) => {
	const { t } = useTranslation()
	const [search, setSearch] = useState('')

	const onSearch = debounce(setSearch, 450)
	const searchQuery = search || (value && !search ? value : undefined)

	const {
		remappedData: geos,
		isLoading,
		isError,
		fetchNextPage
	} = useGetGeos(
		(() => {
			if (searchQuery) {
				return {
					geoCode: searchQuery,
					...(viewOnly && { type: 'view_only' })
				}
			}
			if (viewOnly) {
				return { type: 'view_only' }
			}
			return undefined
		})()
	)

	return (
		<ComboboxProvider setValue={onSearch}>
			<SelectProvider defaultValue='' setValue={onChange} value={value}>
				<div className='flex w-full flex-col gap-1.5'>
					<SelectTrigger
						className='w-full'
						displayValue={(value) =>
							geos.find((geo) => geo.geoCode === value)?.geoCode
						}
						placeholder={t('subgeo:select_geo')}
					/>
					<InputHelpText error={errorMessage} />
				</div>
				<SelectComboboxPopover>
					<SelectComboboxInput autoFocus />
					<SelectComboboxList>
						{(() => {
							if (isLoading) {
								return (
									<div className='flex flex-col gap-1'>
										{Array.from(
											{ length: 6 },
											(_, index) => (
												<Skeleton
													height={22}
													key={`skeleton-${index + 1}`}
												/>
											)
										)}
									</div>
								)
							}
							if (isError) {
								return (
									<InputHelpText
										error={t('errors:default')}
									/>
								)
							}
							if (geos.length === 0) {
								return (
									<InputHelpText
										helpText={t('common:no_results')}
									/>
								)
							}
							return (
								<SelectVirtualList
									data={geos}
									endReached={fetchNextPage}
									itemContent={(_, geo) => (
										<SelectItem
											key={geo.id}
											value={geo.geoCode}
										>
											{geo.geoCode}
										</SelectItem>
									)}
									overscan={100}
									totalCount={geos.length}
								/>
							)
						})()}
					</SelectComboboxList>
				</SelectComboboxPopover>
			</SelectProvider>
		</ComboboxProvider>
	)
}
